import { useState } from 'react';
import { useQueryFetch } from 'sonata-react-components';

import VisitationMetricTrends from '@/components/driveToStore/AggregatedReport/VisitationMetricTrends/VisitationMetricTrends';
import KpiOverview from '@/components/driveToStore/common/KpiOverview/KpiOverview';
import ReportHeader from '@/components/driveToStore/common/ReportHeader/ReportHeader';
import TrafficAnalysis from '@/components/driveToStore/common/TrafficAnalysis/TrafficAnalysis';
import PoiSelectionModal from '@/components/driveToStore/PoiSelectionModal/PoiSelectionModal';
import {
  getAggregatedFootfallLineItemData,
  getFootfallStats,
  getLineItemComparison,
  getPoiListWithUplift
} from '@/services/footfall.service';
import AggregatedReportSkeleton from './AggregatedReportSkeleton';
import BrandStoreList from './BrandStoreSummary/BrandStoreSummary';
import LineItemComparisonTable from './components/LineItemComparisonTable/LineItemComparisonTable';
import StoreDetailTable from './components/StoreDetailTable/StoreDetailTable';

import type {
  AggregatedFootfallLineItemDataResponse,
  FootfallStatsResponse,
  LineItemComparisonRow,
  PoiWithUplift
} from '@/services/footfall.service';

import './aggregatedReport.scss';

type Props = {
  entityType: string;
  entityId: string;
  country?: string;
  sharedReportFullAccess?: boolean;
  userId: string;
  venueIds?: string[];
  lineItemIds?: string[];
};

export default function AggregatedReport({
  entityType,
  entityId,
  country,
  sharedReportFullAccess,
  userId,
  venueIds,
  lineItemIds
}: Readonly<Props>) {
  const [isAllReportsModalOpen, setIsAllReportsModalOpen] = useState(false);
  const entityIds = entityId.split(',').filter(Boolean);
  const effectiveLineItemIds = lineItemIds?.length ? lineItemIds : entityIds;
  const scopedEntityId = effectiveLineItemIds.join(',');

  const {
    data: poisWithUplift = [],
    isLoading: isLoadingPois,
    status: poisStatus
  } = useQueryFetch<PoiWithUplift[]>(
    ['footfall-poi-list-with-uplift', entityType, scopedEntityId, userId, country],
    async () => await getPoiListWithUplift(entityType, scopedEntityId, userId, { country })
  );

  if (poisStatus === 'error') {
    throw new Error('Failed to load the POI list with uplift for the aggregated footfall report');
  }

  const effectiveVenueIds = venueIds?.length
    ? venueIds
    : poisWithUplift
        .filter(p => (p.upliftPercentage ?? 0) > 0)
        .map(p => p.venueId)
        .filter((id): id is string => Boolean(id));

  const { data: lineItemData } = useQueryFetch<AggregatedFootfallLineItemDataResponse | null>(
    ['footfall-lineItemData', entityType, entityId, country],
    async () =>
      await getAggregatedFootfallLineItemData(entityType, entityId, userId, country, { includePoiCount: true })
  );

  const {
    data: statsData,
    isLoading: isLoadingStats,
    status: statsStatus
  } = useQueryFetch<FootfallStatsResponse | null>(
    ['footfall-aggregated-stats', entityType, scopedEntityId, userId, country, effectiveVenueIds.join()],
    async () => await getFootfallStats(entityType, scopedEntityId, userId, country, effectiveVenueIds),
    { enabled: !!venueIds?.length || !isLoadingPois }
  );

  if (statsStatus === 'error') {
    throw new Error('Failed to load footfall stats for the aggregated report');
  }

  const isMultiLineItem = entityIds.length > 1;
  const {
    data: lineItemComparisonData,
    isLoading: isLoadingLineItemComparison,
    status: lineItemComparisonStatus,
    refetch: refetchLineItemComparison
  } = useQueryFetch<{ items: LineItemComparisonRow[] } | null>(
    ['footfall-lineItemComparison', entityType, entityId, userId, country],
    async () => await getLineItemComparison(entityType, entityId, userId, country),
    { enabled: isMultiLineItem }
  );

  const entityCountries = lineItemData?.lineItemData?.countries ?? [];
  const displayPois = poisWithUplift.filter(p => p.venueId != null && effectiveVenueIds.includes(p.venueId));

  const isLoading = isLoadingPois || isLoadingStats || isLoadingLineItemComparison;

  return (
    <div className="aggregatedReport">
      <ReportHeader
        entityIds={entityIds}
        entityType={entityType}
        lineItem={lineItemData?.lineItemData}
        lineItemIds={entityIds}
        onAllReports={() => setIsAllReportsModalOpen(true)}
        pois={displayPois}
        sharedReportFullAccess={sharedReportFullAccess}
        variant="aggregated"
      />
      <PoiSelectionModal
        country={country}
        entityCountries={entityCountries}
        entityId={entityId}
        entityType={entityType}
        isOpen={isAllReportsModalOpen}
        lineItemName={lineItemData?.lineItemData?.name}
        mode="steps"
        onClose={() => setIsAllReportsModalOpen(false)}
        userId={userId}
      />
      {isLoading || !statsData ? (
        <AggregatedReportSkeleton isMultiLineItem={isMultiLineItem} />
      ) : (
        <div className="aggregatedReport__content">
          <div className="aggregatedReport__topRow">
            <div className="aggregatedReport__storeListSlot">
              <BrandStoreList
                entityCountries={entityCountries}
                entityId={entityId}
                entityType={entityType}
                lineItemName={lineItemData?.lineItemData?.name}
                pois={displayPois}
                sharedReportFullAccess={sharedReportFullAccess}
                showUpliftOnlyTag={!venueIds?.length}
                userId={userId}
              />
            </div>
            <div className="aggregatedReport__kpiSlot">
              <KpiOverview data={statsData!.kpiOverview} isAggregated />
            </div>
          </div>
          {isMultiLineItem && (
            <div className="aggregatedReport__lineItemComparisonRow">
              <LineItemComparisonTable
                country={country}
                entityId={entityId}
                entityType={entityType}
                isError={lineItemComparisonStatus === 'error'}
                items={lineItemComparisonData?.items ?? []}
                onRetry={() => refetchLineItemComparison()}
                sharedReportFullAccess={sharedReportFullAccess}
                userId={userId}
              />
            </div>
          )}
          <div className="aggregatedReport__trafficRow">
            <TrafficAnalysis
              campaignEnd={lineItemData?.lineItemData.endDate ?? ''}
              campaignStart={lineItemData?.lineItemData.startDate ?? ''}
              entityId={scopedEntityId}
              entityType={entityType}
              timezone={lineItemData?.lineItemData.timezone ?? 'UTC'}
              userId={userId}
              venueIds={effectiveVenueIds}
            />
          </div>
          <div className="aggregatedReport__visitationRow">
            <VisitationMetricTrends
              campaignEnd={lineItemData?.lineItemData.endDate ?? ''}
              campaignStart={lineItemData?.lineItemData.startDate ?? ''}
              entityId={scopedEntityId}
              entityType={entityType}
              lineItems={lineItemData?.lineItemData.lineItems ?? []}
              pois={displayPois}
              sharedReportFullAccess={sharedReportFullAccess}
              timezone={lineItemData?.lineItemData.timezone ?? 'UTC'}
              userId={userId}
              venueIds={effectiveVenueIds}
            />
          </div>
          <div className="aggregatedReport__storeDetailRow">
            <StoreDetailTable
              country={country}
              entityId={scopedEntityId}
              entityType={entityType}
              userId={userId}
              venueIds={effectiveVenueIds}
            />
          </div>
        </div>
      )}
    </div>
  );
}
